import { Field, ImageField, LinkField } from "@sitecore-jss/sitecore-jss-nextjs";
import { Box, Container, Heading, Text, keyframes } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import useImageSizes from "hooks/useImageSizes";
import breakpoints from "themes/foundations/breakpoints";
import LinkButton from "components/ui/LinkButton";
import BundleIcon from "components/ui/BundleIcon";
import SplitType from "split-type";
import SCImage from "components/sc/SCImage";
import { mapGradient } from "themes/utils/mapGradient";
import { gradients } from "themes/foundations/color";

interface Fields {
    Id: string;
    Headline: Field<string>;
    SubHeadline: Field<string>;
    SliderText: Field<string>;
    CTA: LinkField;
    BackgroundVideoMobile?: Field<string>;
    BackgroundVideoDesktop?: Field<string>;
    BackgroundImageMobile?: ImageField;
    BackgroundImageDesktop?: ImageField;
}

export type HeroCarouselElementProps = {
    id: string;
    fields: Fields;
    index: number;
    currentSlideIndex: number;
    firstLoad: boolean;
};

type HeroCarouselBackgroundProps = {
    fields: Fields;
    index: number;
    currentSlideIndex: number;
};

const animateIn = keyframes`
    from {
		transform: translateY(-1.4em);
        opacity: 0.5;
    }
    to {
		transform: translateY(0);
		opacity: 1;
    }
`;

const HeroBackground = (props: HeroCarouselBackgroundProps) => {
    const imageMobile = props.fields?.BackgroundImageMobile;
    const imageDesktop = props.fields?.BackgroundImageDesktop;
    const videoMobile = props.fields?.BackgroundVideoMobile?.value;
    const videoDesktop = props.fields?.BackgroundVideoDesktop?.value;

    const sizes = useImageSizes({ base: 2 / 3 }, false);
    const videoPoster =
        imageMobile?.value?.src && imageMobile?.value?.src !== "" && imageMobile?.value?.src;

    return (
        <Box
            sx={{
                position: "relative",
                height: "full",
                bgGradient: mapGradient(gradients.spectrum, "radial"),
                bgSize: "300% 200%",
                bgPos: "90% 0",
                zIndex: 0,
                video: {
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                },
                "> div:first-of-type, > img:first-of-type": {
                    display: { base: "block", lg: "none" },
                },
                "> div:last-of-type, > img:last-of-type": {
                    display: { base: "none", lg: "block" },
                },
                "> div, .sc-image-wrapper": {
                    height: "full",
                },
            }}
        >
            <>
                {videoMobile !== "" && videoDesktop !== "" && (
                    <video
                        autoPlay={props.currentSlideIndex === props.index}
                        loop
                        muted
                        playsInline
                        poster={`${videoPoster}`}
                    >
                        <source
                            media={`(min-width: ${String(breakpoints.lg)})`}
                            src={videoDesktop}
                            type="video/mp4"
                        />
                        <source src={videoMobile} type="video/mp4" />
                    </video>
                )}
                {imageMobile?.value?.src !== "" &&
                    imageDesktop?.value?.src !== "" &&
                    videoMobile === "" &&
                    videoDesktop === "" && (
                        <>
                            <SCImage
                                imageField={imageMobile}
                                fill
                                sizes={sizes}
                                contentEditable={false}
                                priority
                                sx={{
                                    width: "full",
                                    height: "full",
                                    objectFit: "cover",
                                    display: { base: "block", lg: "none" },
                                }}
                            />
                            <SCImage
                                imageField={imageDesktop}
                                fill
                                sizes={sizes}
                                contentEditable={false}
                                priority
                                sx={{
                                    width: "full",
                                    height: "full",
                                    objectFit: "cover",
                                    display: { base: "none", lg: "block" },
                                }}
                            />
                        </>
                    )}
            </>
        </Box>
    );
};

const HeroCarouselElement = (props: HeroCarouselElementProps) => {
    const [splittedText, setSplittedText] = useState<HTMLElement[] | null>();

    const isShown = props.currentSlideIndex === props.index;
    const runAnimation = splittedText && !props.firstLoad;

    useEffect(() => {
        // Update SplitType instance with the new content
        const text = new SplitType(`#hero_heading_${props.index}`, {
            types: "lines",
        });
        setSplittedText(text.lines);

        return () => {
            // Revert SplitType instance when component unmounts or when content changes
            text.revert();
        };
    }, [props.currentSlideIndex, props.index]);

    return (
        <Box
            transition={`opacity 0.3s linear`}
            opacity={isShown ? 1 : 0}
            sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "full",
                    width: "full",
                    zIndex: 1,
                    pt: [
                        "calc(var(--sizes-headerMobileTopHeight) + var(--space-sp24))",
                        null,
                        null,
                        "calc(var(--sizes-headerTopHeight) + var(--space-sp128))",
                    ],
                }}
            >
                {props.fields?.Headline?.value && (
                    <Container
                        display="grid"
                        gridTemplateColumns={{ lg: "repeat(12, 1fr)" }}
                        px={"clamp(2.625rem, 0.028rem + 11.08vw, 10rem)"}
                        sx={{
                            position: [null, null, null, "absolute"],
                            top: { lg: "25%" },
                            left: { lg: "0" },
                            right: { lg: "0" },
                            transform: { lg: "translateY(-50%)" },
                        }}
                    >
                        <Heading
                            id={`hero_heading_${props.index}`}
                            as="h1"
                            gridColumn={{ lg: "1/-3" }}
                            size="h1"
                            pointerEvents="none"
                            opacity={runAnimation ? 0 : 1}
                            sx={{
                                fontKerning: "none",
                            }}
                            width="100%"
                            position={runAnimation ? "absolute" : "relative"}
                            overflow="hidden"
                        >
                            {props.fields?.Headline?.value}
                        </Heading>
                        {runAnimation && (
                            <Heading
                                as="div"
                                gridColumn={{ lg: "1/-3" }}
                                size="h1"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "tight",
                                    fontKerning: "none",
                                    lineHeight: "none",
                                }}
                            >
                                {splittedText.map((line, index) => {
                                    const delay = 0.1 * index;
                                    return (
                                        <Box
                                            key={`hero_heading_${props.id}_${line.textContent}`}
                                            overflow="hidden"
                                        >
                                            <Box
                                                transform={"translateY(-1.4em)"}
                                                {...(isShown && {
                                                    animation: `${animateIn} 0.9s ${delay}s ease-out forwards`,
                                                })}
                                            >
                                                {line.innerText}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Heading>
                        )}

                        {props.fields?.CTA.value.href && props.fields?.CTA.value.text && (
                            <LinkButton
                                sx={{ mt: "sp20", width: "fit-content" }}
                                display={{ base: "none", lg: "flex" }}
                                href={props.fields?.CTA.value.href}
                                size="sm"
                                gridRow="2"
                                gridColumn={"1/-3"}
                                variant={"secondary"}
                                rightIcon={<BundleIcon name="ArrowForward" />}
                                transition="all 0.3s, opacity 0.3s ease"
                            >
                                {props.fields?.CTA.value.text}
                            </LinkButton>
                        )}
                    </Container>
                )}
                <Container
                    sx={{
                        position: [null, null, null, "absolute"],
                        width: "100%",
                        mt: { base: "sp48", lg: "0" },
                        top: { lg: "75%" },
                        left: { lg: "0" },
                        right: { lg: "0" },
                        transform: { lg: "translateY(-50%)" },
                        display: { lg: "grid" },
                        gridTemplateColumns: { lg: "repeat(12, 1fr)" },
                        px: "clamp(2.625rem, 0.028rem + 11.08vw, 10rem)",
                    }}
                >
                    <Box transition="opacity 0.3s ease" gridColumn={{ lg: "7/-1" }}>
                        <Text size={"md"}>{props.fields?.SubHeadline.value}</Text>
                        {props.fields?.CTA.value.href && props.fields?.CTA.value.text && (
                            <LinkButton
                                sx={{ mt: "sp40", width: "fit-content" }}
                                display={{ base: "flex", lg: "none" }}
                                href={props.fields?.CTA.value.href}
                                size="sm"
                                variant={"secondary"}
                                rightIcon={<BundleIcon name="ArrowForward" />}
                            >
                                {props.fields?.CTA.value.text}
                            </LinkButton>
                        )}
                    </Box>
                </Container>
            </Box>

            <HeroBackground {...props} />
        </Box>
    );
};
export default HeroCarouselElement;
